import { format, isValid, parseISO } from 'date-fns'

import { isObjectLike, mapApiListResponse } from '@/utils/api/response-helpers'
import type { ApplicationResponseDTO } from '@/utils/api/membership-service/models'
import { ApplicationResponseDTOStatus as ApplicationStatus } from '@/utils/api/membership-service/models'

import type { AdminApplication, AdminApplicationRow } from './types'

const STATUS_VALUES = new Set(Object.values(ApplicationStatus))
const UGX_FORMATTER = new Intl.NumberFormat('en-UG', { style: 'currency', currency: 'UGX', maximumFractionDigits: 0 })

function normaliseStatus(value: unknown): ApplicationStatus {
  if (typeof value === 'string' && STATUS_VALUES.has(value as ApplicationStatus)) {
    return value as ApplicationStatus
  }
  return ApplicationStatus.PENDING
}

function normaliseNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string') {
    const cleaned = value.replace(/,/g, '')
    const parsed = Number(cleaned)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }
  return null
}

export function normaliseApplication(input: unknown): AdminApplication | null {
  if (!input || typeof input !== 'object') {
    return null
  }

  if (isObjectLike(input)) {
    const nestedApplication = (input as { application?: unknown }).application
    if (nestedApplication && nestedApplication !== input) {
      const resolved = normaliseApplication(nestedApplication)
      if (resolved) {
        return resolved
      }
    }

    const nestedData = (input as { data?: unknown }).data
    if (nestedData && nestedData !== input) {
      const resolved = normaliseApplication(nestedData)
      if (resolved) {
        return resolved
      }
    }
  }

  const application = input as ApplicationResponseDTO

  if (typeof application.id !== 'number') {
    return null
  }

  const applicationRecord = application as Record<string, unknown>

  let categoryName = 'Unassigned'
  if (typeof application.categoryName === 'string' && application.categoryName.length > 0) {
    categoryName = application.categoryName
  } else {
    const category = applicationRecord.category
    if (isObjectLike(category)) {
      const categoryRecord = category as Record<string, unknown>
      const directName = categoryRecord.categoryName
      const fallbackName = categoryRecord.name
      if (typeof directName === 'string' && directName.length > 0) {
        categoryName = directName
      } else if (typeof fallbackName === 'string' && fallbackName.length > 0) {
        categoryName = fallbackName
      }
    }
  }

  const phoneNumbers =
    typeof application.phoneNumbers === 'string'
      ? application.phoneNumbers
      : typeof applicationRecord.phoneNumber === 'string'
        ? (applicationRecord.phoneNumber as string)
        : ''

  const emailAddress =
    typeof application.emailAddress === 'string'
      ? application.emailAddress
      : typeof applicationRecord.email === 'string'
        ? (applicationRecord.email as string)
        : ''

  let placeOfResident: string | undefined
  if (typeof application.placeOfResident === 'string' && application.placeOfResident.length > 0) {
    placeOfResident = application.placeOfResident
  } else if (typeof applicationRecord.placeOfResidence === 'string' && applicationRecord.placeOfResidence.length > 0) {
    placeOfResident = applicationRecord.placeOfResidence as string
  }

  return {
    id: application.id,
    userId: typeof application.userId === 'number' ? application.userId : null,
    status: normaliseStatus((application as { status?: unknown }).status),
    membershipNumber:
      typeof application.membershipNumber === 'string' && application.membershipNumber.length > 0
        ? application.membershipNumber
        : typeof applicationRecord.membershipNumber === 'string' && applicationRecord.membershipNumber.length > 0
          ? (applicationRecord.membershipNumber as string)
          : null,
    categoryName,
    submittedAt: typeof application.submittedAt === 'string' ? application.submittedAt : null,
    createdAt: typeof application.createdAt === 'string' ? application.createdAt : null,
    updatedAt: typeof application.updatedAt === 'string' ? application.updatedAt : null,
    firstName: typeof application.firstName === 'string' ? application.firstName : '',
    middleName: typeof application.middleName === 'string' && application.middleName.length > 0 ? application.middleName : undefined,
    lastName: typeof application.lastName === 'string' ? application.lastName : '',
    emailAddress,
    phoneNumbers,
    dateOfBirth: typeof application.dateOfBirth === 'string' ? application.dateOfBirth : null,
    gender: application.gender,
    placeOfResident,
    organisation: typeof application.organisation === 'string' && application.organisation.length > 0 ? application.organisation : undefined,
    studentId: typeof application.studentId === 'string' && application.studentId.length > 0 ? application.studentId : undefined,
    institution: typeof application.institution === 'string' && application.institution.length > 0 ? application.institution : undefined,
    graduationYear: normaliseNumber(application.graduationYear),
    companyRegistrationNumber:
      typeof applicationRecord.companyRegistrationNumber === 'string' && (applicationRecord.companyRegistrationNumber as string).length > 0
        ? (applicationRecord.companyRegistrationNumber as string)
        : undefined,
    companySize:
      typeof applicationRecord.companySize === 'string' && (applicationRecord.companySize as string).length > 0
        ? (applicationRecord.companySize as string)
        : undefined,
    businessSector:
      typeof applicationRecord.businessSector === 'string' && (applicationRecord.businessSector as string).length > 0
        ? (applicationRecord.businessSector as string)
        : undefined,
    yearsOfExperience: normaliseNumber(application.yearsOfExperience),
    professionalLicenseNumber:
      typeof application.professionalLicenseNumber === 'string' && application.professionalLicenseNumber.length > 0
        ? application.professionalLicenseNumber
        : undefined,
    // attachment: typeof application.attachment === 'string' && application.attachment.length > 0 ? application.attachment : undefined,
    employmentHistory: Array.isArray(application.employmentHistory) ? application.employmentHistory : [],
    education: Array.isArray(application.education) ? application.education : [],
    affiliations: Array.isArray(application.affiliations) ? application.affiliations : [],
    awards: Array.isArray(application.awards) ? application.awards : [],
    documents: Array.isArray(application.documents) ? application.documents : [],
    membershipStartAt:
      typeof applicationRecord.membershipStartAt === 'string'
        ? (applicationRecord.membershipStartAt as string)
        : undefined,
    membershipExpiryAt:
      typeof applicationRecord.membershipExpiryAt === 'string'
        ? (applicationRecord.membershipExpiryAt as string)
        : undefined,
    lastRenewedAt:
      typeof applicationRecord.lastRenewedAt === 'string'
        ? (applicationRecord.lastRenewedAt as string)
        : undefined,
    renewalCount: normaliseNumber(applicationRecord.renewalCount),
    expired:
      typeof applicationRecord.expired === 'boolean'
        ? (applicationRecord.expired as boolean)
        : undefined,
    membershipAmount: normaliseNumber(
      applicationRecord.amount ??
        applicationRecord.amountPaid ??
        applicationRecord.paymentAmount ??
        applicationRecord.membershipAmount,
    ),
  }
}

export function mapApplicationsResponse(payload: unknown): AdminApplication[] {
  return mapApiListResponse(payload, normaliseApplication)
    .filter((item): item is AdminApplication => item !== null)
}

export function createApplicationRow(application: AdminApplication): AdminApplicationRow {
  const submittedAt = application.submittedAt ?? application.createdAt ?? null
  const membershipDuration = computeDurationLabel(application.membershipStartAt ?? null, application.membershipExpiryAt ?? null)
  const remainingDuration = computeRemainingDurationLabel(application.membershipExpiryAt ?? null)
  const remainingDays = computeRemainingDays(application.membershipExpiryAt ?? null)

  return {
    id: application.id,
    name: formatFullName(application.firstName, application.middleName, application.lastName),
    email: application.emailAddress || '—',
    phone: application.phoneNumbers || '—',
    category: application.categoryName || '—',
    status: application.status,
    submittedAt,
    updatedAt: application.updatedAt,
    membershipNumber: application.membershipNumber ?? null,
    membershipStartAt: application.membershipStartAt ?? null,
    membershipExpiryAt: application.membershipExpiryAt ?? null,
    membershipDuration,
    remainingDuration,
    remainingDays,
    membershipAmount: application.membershipAmount ?? null,
  }
}

export function formatFullName(firstName: string, middleName: string | undefined, lastName: string): string {
  const parts = [firstName, middleName, lastName].filter((part) => Boolean(part && part.trim().length > 0))
  if (parts.length === 0) {
    return 'Unnamed applicant'
  }
  return parts.join(' ')
}

export function parseDate(value?: string | null): Date | null {
  if (!value) return null
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : null
}

export function formatDate(value?: string | null, pattern = 'PPP'): string | null {
  const parsed = parseDate(value)
  if (!parsed) return null
  return format(parsed, pattern)
}

export function formatDateTime(value?: string | null, pattern = 'PPPp'): string | null {
  const parsed = parseDate(value)
  if (!parsed) return null
  return format(parsed, pattern)
}

export function formatCurrencyUGX(value?: number | null): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—'
  return UGX_FORMATTER.format(value)
}

export function computeRemainingDays(endDate: string | null): number | null {
  const end = parseDate(endDate)
  if (!end) return null

  const now = new Date()
  const diffMs = end.getTime() - now.getTime()
  if (diffMs <= 0) return 0

  return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
}

export function computeDurationLabel(startDate: string | null, endDate: string | null): string | null {
  const start = parseDate(startDate)
  const end = parseDate(endDate)
  if (!start || !end) return null

  const diffMs = end.getTime() - start.getTime()
  if (diffMs <= 0) return null

  const totalDays = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1
  const months = Math.floor(totalDays / 30)
  const years = Math.floor(months / 12)
  if (years >= 1) {
    return `${years} year${years === 1 ? '' : 's'}`
  }
  if (months >= 1) {
    return `${months} month${months === 1 ? '' : 's'}`
  }

  return `${totalDays} day${totalDays === 1 ? '' : 's'}`
}

export function computeRemainingDurationLabel(endDate: string | null): string | null {
  const totalDays = computeRemainingDays(endDate)
  if (totalDays === null) return null
  if (totalDays <= 0) return '0 days'

  const months = Math.floor(totalDays / 30)
  const years = Math.floor(months / 12)

  if (years >= 1) {
    return `${years} year${years === 1 ? '' : 's'}`
  }
  if (months >= 1) {
    return `${months} month${months === 1 ? '' : 's'}`
  }

  return `${totalDays} day${totalDays === 1 ? '' : 's'}`
}
